import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useWorkItemByNumber } from '@/hooks'
import { normalizeWorkItemStatus, type WorkItem } from '@/types'
import { X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

interface AddWorkItemDialogProps {
  projectId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onResolved: (workItem: WorkItem) => void
}

export function AddWorkItemDialog({ projectId, open, onOpenChange, onResolved }: AddWorkItemDialogProps) {
  const [inputValue, setInputValue] = useState('')
  const [validationError, setValidationError] = useState<string | null>(null)
  const [pendingNumber, setPendingNumber] = useState<number | null>(null)
  const resolvedNumberRef = useRef<number | null>(null)

  const { data, error, isFetching } = useWorkItemByNumber(projectId, pendingNumber)

  useEffect(() => {
    if (!open) {
      setInputValue('')
      setValidationError(null)
      setPendingNumber(null)
      resolvedNumberRef.current = null
    }
  }, [open])

  const isBacklogStatus = !data || normalizeWorkItemStatus(data.status) !== null
  const intentErrorMessage =
    data && !isFetching && !isBacklogStatus
      ? "This work item is an Intent and hasn't reached the Backlog yet. Only Backlog items (New, In Progress, or Completed) can be linked to a test case."
      : null

  useEffect(() => {
    if (!open || pendingNumber == null || isFetching || !data) return
    if (resolvedNumberRef.current === pendingNumber) return
    if (!isBacklogStatus) return
    resolvedNumberRef.current = pendingNumber
    onResolved(data)
    onOpenChange(false)
  }, [open, data, pendingNumber, isFetching, isBacklogStatus, onResolved, onOpenChange])

  const handleAdd = () => {
    const trimmed = inputValue.trim()
    const parsed = Number(trimmed)
    if (!trimmed || !Number.isInteger(parsed) || parsed <= 0) {
      setValidationError('Enter a valid Work Item number.')
      return
    }
    setValidationError(null)
    setPendingNumber(parsed)
  }

  const lookupError = pendingNumber != null && !isFetching && error
  const lookupErrorMessage = lookupError
    ? (error as { response?: { status?: number } })?.response?.status === 404
      ? "Work item with this number doesn't exist."
      : 'Failed to look up work item. Try again.'
    : null

  const activeError = validationError
    ? { testId: 'add-work-item-validation-error', message: validationError }
    : lookupErrorMessage
      ? { testId: 'add-work-item-lookup-error', message: lookupErrorMessage }
      : intentErrorMessage
        ? { testId: 'add-work-item-intent-error', message: intentErrorMessage }
        : null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[480px] rounded-2xl pt-6 pr-8 pb-8 pl-8 gap-2.5 border-none shadow-lg" hideCloseButton>
        <DialogHeader className="h-10 flex-row items-center justify-between space-y-0 mb-4">
          <DialogTitle className="text-xl font-bold leading-[1.3] tracking-[0.015em] text-primary">
            Add Work Item
          </DialogTitle>
          <DialogClose className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none">
            <X className="h-5 w-5" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogHeader>
        <DialogDescription className="text-sm font-normal leading-[1.6] tracking-[0.015em] text-muted-foreground">
          Enter the Work Item number to link it to this test case.
        </DialogDescription>
        <div className="space-y-2 pt-3">
          <Label htmlFor="add-work-item-number">Work Item ID</Label>
          <Input
            id="add-work-item-number"
            type="number"
            min={1}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="e.g. 2"
            data-testid="add-work-item-id-input"
            autoFocus
          />
          {activeError && (
            <p role="alert" className="text-sm text-destructive" data-testid={activeError.testId}>
              {activeError.message}
            </p>
          )}
        </div>
        <DialogFooter className="pt-4">
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            data-testid="add-work-item-cancel-button"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleAdd}
            disabled={isFetching}
            data-testid="add-work-item-submit-button"
          >
            {isFetching ? 'Resolving…' : 'Add'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
